import { useEffect, useState, type ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  AlertTriangle,
  X,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  useGetReconciliationJobStatusQuery,
  useCancelJobMutation,
  AsyncJobStatus,
  type AsyncJobStatusType,
} from '@/services/tax-reconciliation';
import type { ConciliacaoResponseDto } from '@/services/conciliacao-fiscal';

interface AsyncJobStatusProps {
  jobId: string;
  onComplete: (result: ConciliacaoResponseDto) => void;
  onCancel: () => void;
}

const STATUS_ICON_MAP: Record<string, ReactNode> = {
  [AsyncJobStatus.COMPLETED]: <CheckCircle className="h-5 w-5 text-green-500" />,
  [AsyncJobStatus.FAILED]: <XCircle className="h-5 w-5 text-red-500" />,
  [AsyncJobStatus.CANCELLED]: <X className="h-5 w-5 text-gray-500" />,
  [AsyncJobStatus.PROCESSING]: <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />,
  [AsyncJobStatus.PENDING]: <Clock className="h-5 w-5 text-yellow-500" />,
};

const STATUS_TEXT_MAP: Record<string, string> = {
  [AsyncJobStatus.PENDING]: 'Aguardando Processamento',
  [AsyncJobStatus.PROCESSING]: 'Processando Dados',
  [AsyncJobStatus.COMPLETED]: 'Processamento Concluído',
  [AsyncJobStatus.FAILED]: 'Processamento Falhou',
  [AsyncJobStatus.CANCELLED]: 'Processamento Cancelado',
};

const STATUS_VARIANT_MAP: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  [AsyncJobStatus.FAILED]: 'destructive',
  [AsyncJobStatus.COMPLETED]: 'default',
  [AsyncJobStatus.CANCELLED]: 'secondary',
};

export default function AsyncJobStatusComponent({ jobId, onComplete, onCancel }: AsyncJobStatusProps) {
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const [estimatedTime, setEstimatedTime] = useState<string>('');

  const {
    data: rawJobStatus,
    error,
    refetch
  } = useGetReconciliationJobStatusQuery(jobId, {
    pollingInterval: pollingEnabled ? 5000 : 0,
  });

  const normalizedStatus = rawJobStatus?.status?.toUpperCase() as AsyncJobStatusType | undefined;

  const jobStatus = rawJobStatus ? {
    ...rawJobStatus,
    status: normalizedStatus,
  } : undefined;

  const [cancelJob, { isLoading: isCancelling }] = useCancelJobMutation();

  const progress = jobStatus?.progress || 0;
  const isActive = normalizedStatus === AsyncJobStatus.PENDING || normalizedStatus === AsyncJobStatus.PROCESSING;
  const isFinished = normalizedStatus === AsyncJobStatus.COMPLETED || normalizedStatus === AsyncJobStatus.FAILED || normalizedStatus === AsyncJobStatus.CANCELLED;

  useEffect(() => {
    if (progress > 0 && progress < 100) {
      const remainingProgress = 100 - progress;
      const estimatedMinutes = Math.ceil(remainingProgress / 10);
      setEstimatedTime(`~${estimatedMinutes} minutos restantes`);
      return;
    }
    setEstimatedTime('');
  }, [progress]);

  useEffect(() => {
    const result = (jobStatus as any)?.paginatedResult || (jobStatus as any)?.result;

    if (normalizedStatus === AsyncJobStatus.COMPLETED && result) {
      setPollingEnabled(false);
      onComplete(result);
      return;
    }

    if (normalizedStatus === AsyncJobStatus.FAILED) {
      setPollingEnabled(false);
    }

    if (normalizedStatus === AsyncJobStatus.CANCELLED) {
      setPollingEnabled(false);
      // Auto-dismiss after 3s so user sees the cancelled status briefly
      const timer = setTimeout(() => onCancel(), 3000);
      return () => clearTimeout(timer);
    }
  }, [jobStatus, onComplete, onCancel, normalizedStatus]);

  const handleCancel = async () => {
    try {
      await cancelJob(jobId).unwrap();
      setPollingEnabled(false);
      // Refetch to show CANCELLED status in the UI before dismissing
      await refetch();
    } catch (err) {
      console.error('Erro ao cancelar job:', err);
      // Even if the API call fails, stop polling and dismiss
      setPollingEnabled(false);
      onCancel();
    }
  };

  const handleRetry = () => {
    setPollingEnabled(true);
    refetch();
  };

  const statusIcon = error
    ? <XCircle className="h-5 w-5 text-red-500" />
    : STATUS_ICON_MAP[normalizedStatus || ''] || <Clock className="h-5 w-5 text-yellow-500" />;

  const statusText = error
    ? 'Erro de Conexão'
    : STATUS_TEXT_MAP[normalizedStatus || ''] || 'Status Desconhecido';

  const statusVariant = error
    ? 'destructive' as const
    : STATUS_VARIANT_MAP[normalizedStatus || ''] || 'outline' as const;

  return (
    <Card className="p-6 border-2 border-blue-200 bg-blue-50/50">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {statusIcon}
            <div>
              <h3 className="font-semibold text-lg">{statusText}</h3>
              <p className="text-sm text-muted-foreground">
                Job ID: {jobId}
              </p>
            </div>
          </div>

          <Badge variant={statusVariant}>
            {normalizedStatus || 'UNKNOWN'}
          </Badge>
        </div>

        {/* Progress Bar */}
        {isActive && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progresso</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-500 relative overflow-hidden"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
              </div>
            </div>
            {estimatedTime && (
              <p className="text-xs text-muted-foreground text-center">
                {estimatedTime}
              </p>
            )}
          </div>
        )}

        {/* Status Details */}
        <div className="bg-white/70 rounded-lg p-4 space-y-2">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Iniciado:</span>
              <span className="ml-2">
                {format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </span>
            </div>
            <div>
              <span className="font-medium">Tipo:</span>
              <span className="ml-2">Conciliação Fiscal</span>
            </div>
          </div>

          {jobStatus?.error && (
            <div className="bg-red-50 border border-red-200 rounded p-2">
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">Erro:</span>
              </div>
              <p className="text-red-600 text-sm mt-1">{jobStatus.error}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          {isActive && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleCancel}
              disabled={isCancelling}
            >
              {isCancelling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelando...
                </>
              ) : (
                <>
                  <X className="mr-2 h-4 w-4" />
                  Cancelar Processamento
                </>
              )}
            </Button>
          )}

          {!!error && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Tentar Novamente
            </Button>
          )}

          {isFinished && normalizedStatus !== AsyncJobStatus.COMPLETED && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
            >
              Fechar
            </Button>
          )}
        </div>

        {/* Auto-refresh indicator */}
        {pollingEnabled && isActive && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            Atualizando automaticamente a cada 5 segundos
          </div>
        )}
      </div>
    </Card>
  );
}
